import React, { useState, useEffect } from 'react'
import { Card, Input, Row, Col, Button, Select, message, Tag } from 'antd'
import './index.less'
import { PageHeader } from '@components'
import { getAIQualityCheckPlanList, getAIQualityCheckPlanDetail, getAIQualityCheckPlanVersionList, getAIQualityCheckPlanPrompt, getAIQualityCheckPlanPromptTemplate, saveAIQualityCheckPlanPrompt, bindAgentKey } from '@services/ai-quality-check'
import { AIQualityCheckPlanListItem, AIQualityCheckPlanVersionItem, AIQualityCheckPlanDetailItem } from 'src/types/ai-quality-check'
import { QcType, QcSchemeStatusMap, QcSchemeStatus } from '../quality-check-scheme-detail/constants'
import { generateComplianceRules, generateMocRules, generateScenarioTagRule } from './rules'
import PreviewCompliance from './components/compliance'
import PreviewMoc from './components/moc'
import PreviewScenarioTag from './components/scenarioTag'

function LlmTuningPage () {
  const [showHistoryPrompt, setShowHistoryPrompt] = useState(false)
  const [agentKey, setAgentKey] = useState('')
  const [isEditing, setIsEditing] = useState(false) // 是否处于编辑状态
  const [selectedCheckPlan, setSelectedCheckPlan] = useState({
    id: undefined,
    type: undefined as number | undefined,
    status: undefined,
    detail: undefined as AIQualityCheckPlanDetailItem | undefined,
    prompt: '',
    checkPlanList: [],
    name: '',
  })
  const [selectedCheckPlanHistory, setSelectedCheckPlanHistory] = useState({
    version: undefined,
    detail: undefined as AIQualityCheckPlanDetailItem | undefined,
    prompt: '',
    historyPlanList: [],
  })
  // 方案详情数据缓存
  const [planDetailCache, setPlanDetailCache] = useState<Record<string, AIQualityCheckPlanDetailItem>>({})
  // 方案历史版本数据缓存
  const [historyPlanListCache, setHistoryPlanListCache] = useState<Record<string, AIQualityCheckPlanVersionItem[]>>({})
  // 方案prompt数据缓存
  const [promptCache, setPromptCache] = useState<Record<string, string>>({})
  // 方案prompt模版数据缓存
  const [promptTemplateCache, setPromptTemplateCache] = useState<Record<string, string>>({})

  // 获取质检方案列表
  const getAIQualityCheckPlanOption = async () => {
    const [res, err] = await getAIQualityCheckPlanList({
      statusList: [QcSchemeStatus.optimizing, QcSchemeStatus.optimized],
    })
    if (err) {
      message.error(`获取质检方案失败：${err.message}`)
      return
    }
    const list = res?.list.map((item: AIQualityCheckPlanListItem) => ({
      label: item.name,
      value: item.id,
      planType: item.type,
      status: item.status,
    })) || []
    setSelectedCheckPlan({
      id: list?.[0]?.value,
      type: list?.[0]?.planType || undefined,
      status: list?.[0]?.status || undefined,
      detail: undefined,
      prompt: '',
      checkPlanList: list,
      name: list?.[0]?.label,
    })
  }

  // 生成缓存键
  const generateCacheKey = (planId?: number, planVersionId?: number) => {
    return `${planId || ''}-${planVersionId || ''}`
  }

  const setPlanDetail = (planVersionId?: number, detail?: AIQualityCheckPlanDetailItem) => {
    if (planVersionId) {
      setSelectedCheckPlanHistory(prev => ({
        ...prev,
        detail,
      }))
    } else {
      setSelectedCheckPlan(prev => ({
        ...prev,
        detail,
        status: detail?.status,
      }))
    }
  }

  // 获取质检方案详情
  const getAIQualityCheckPlanDetailById = async (planId?: number, planVersionId?: number) => {
    if (!planId) return
    const cacheKey = generateCacheKey(planId, planVersionId)
    const cachedData = planDetailCache[cacheKey]

    if (cachedData) {
      setPlanDetail(planVersionId, cachedData)
      return
    }

    const [res, err] = await getAIQualityCheckPlanDetail({ plan: [{ planId, planVersionId }] })
    if (err) {
      message.error(`获取质检方案详情失败：${err.message}`)
      return
    }
    const detail = res?.list?.[0]
    setPlanDetailCache(prev => ({
      ...prev,
      [cacheKey]: detail,
    }))
    setPlanDetail(planVersionId, detail)
  }

  // 获取质检方案历史版本
  const getAIQualityCheckPlanOptionHistory = async (planId?: number) => {
    if (!planId) return
    if (historyPlanListCache[planId]) {
      // 默认选中列表中的第一个
      setSelectedCheckPlanHistory(prev => ({
        ...prev,
        version: historyPlanListCache[planId]?.[0]?.id,
        historyPlanList: historyPlanListCache[planId],
      }))
      return
    }

    const [res, err] = await getAIQualityCheckPlanVersionList({ planId })
    if (err) {
      message.error(`获取质检方案历史版本失败：${err.message}`)
      return
    }

    const sortedList = (res?.list || []).sort((a, b) => {
      const dateA = new Date(a.updateTime).getTime()
      const dateB = new Date(b.updateTime).getTime()
      return dateB - dateA
    })

    const dateGroupMap = new Map<string, AIQualityCheckPlanVersionItem[]>()
    sortedList.forEach(item => {
      const date = new Date(item.updateTime)
      if (!isNaN(date.getTime())) {
        const dateStr = `${date.getFullYear()}.${date.getMonth() + 1}.${date.getDate()}`
        if (!dateGroupMap.has(dateStr)) {
          dateGroupMap.set(dateStr, [])
        }
        dateGroupMap.get(dateStr).push(item)
      }
    })

    const list = sortedList.map((item, index) => {
      const date = new Date(item.updateTime)
      const dateStr = `${date.getFullYear()}.${date.getMonth() + 1}.${date.getDate()}`
      const sameDateItems = dateGroupMap.get(dateStr) || []
      const orderInDate = sameDateItems.length - sameDateItems.indexOf(item)
      const label = `${selectedCheckPlan.name}(${dateStr}提交)${sameDateItems.length > 1 ? `(${orderInDate})` : ''}`
      return {
        label,
        value: item.id,
      }
    })
    setSelectedCheckPlanHistory({
      version: list?.[0]?.value,
      detail: undefined,
      prompt: '',
      historyPlanList: list,
    })
    setHistoryPlanListCache({
      ...historyPlanListCache,
      [planId]: list,
    })
    getAIQualityCheckPlanDetailById(planId, list?.[0]?.value)
  }

  // 封装设置prompt的逻辑
  const setPlanPrompt = (planVersionId?: number, prompt?: string) => {
    if (planVersionId) {
      setSelectedCheckPlanHistory(prev => ({
        ...prev,
        prompt,
      }))
    } else {
      setSelectedCheckPlan(prev => ({
        ...prev,
        prompt,
      }))
    }
  }

  // 获取质检方案prompt
  const getAIQualityCheckPrompt = async (planId?: number, planVersionId?: number) => {
    if (!planId) return
    const cacheKey = generateCacheKey(planId, planVersionId)
    const cachedPrompt = promptCache[cacheKey]

    if (cachedPrompt) {
      setPlanPrompt(planVersionId, cachedPrompt)
      return
    }

    const [res, err] = await getAIQualityCheckPlanPrompt({ planId, planVersionId })
    if (err) {
      message.error(`获取质检方案prompt失败：${err.message}`)
      return
    }
    if (res?.prompt?.length > 0) {
      setPlanPrompt(planVersionId, res.prompt)
      setPromptCache({
        ...promptCache,
        [cacheKey]: res.prompt,
      })
    } else if (planVersionId) {
      setPlanPrompt(planVersionId, '无')
      setPromptCache({
        ...promptCache,
        [cacheKey]: '无',
      })
    } else {
      const currentPlan = selectedCheckPlan.checkPlanList.find(item => item.value === planId)
      currentPlan
        ? fetchAIQualityCheckPromptTemplate(currentPlan.planType)
        : message.warning('未找到对应的质检方案类型，无法获取prompt模版')
    }
  }

  // 获取质检方案prompt模版
  const fetchAIQualityCheckPromptTemplate = async (planType?: number) => {
    if (!planType) return
    if (promptTemplateCache[planType]) {
      setSelectedCheckPlan(prev => ({
        ...prev,
        prompt: promptTemplateCache[planType],
      }))
      return
    }

    const [res, err] = await getAIQualityCheckPlanPromptTemplate({ planType })
    if (err) {
      message.error(`获取质检方案prompt模版失败：${err.message}`)
      return
    }
    let promptTemplate = res?.templates?.[0]?.prompt
    const ruleMap = {
      [QcType.compliance]: () => generateComplianceRules(selectedCheckPlan),
      [QcType.moc]: () => generateMocRules(selectedCheckPlan),
      [QcType.scenarioTag]: () => generateScenarioTagRule(selectedCheckPlan),
    }
    const RuleJson = ruleMap[selectedCheckPlan.type]?.() || {}
    const ruleJsonStr = JSON.stringify(RuleJson, null, 2)
    const updatedPrompt = promptTemplate && promptTemplate.replace(/\{\{\.RuleJson\}\}/g, ruleJsonStr)
    promptTemplate = updatedPrompt
    setSelectedCheckPlan(prev => ({
      ...prev,
      prompt: promptTemplate,
    }))
    setPromptTemplateCache({
      ...promptTemplateCache,
      [planType]: promptTemplate,
    })
  }

  // 保存prompt
  const saveAIQualityCheckPrompt = async (planId?: number, completeDebug?: boolean) => {
    const [res, err] = await saveAIQualityCheckPlanPrompt({ planId, prompt: selectedCheckPlan.prompt, completeDebug })
    if (err) {
      message.error(`prompt保存失败：${err.message}`)
      return
    }
    const cacheKey = generateCacheKey(planId)
    if (planDetailCache[cacheKey]) {
      setPlanDetailCache({
        ...planDetailCache,
        [cacheKey]: {
          ...planDetailCache[cacheKey],
          status: QcSchemeStatus.optimized,
        },
      })
    }
    setPromptCache(prev => ({
      ...prev,
      [cacheKey]: selectedCheckPlan.prompt,
    }))
    if (completeDebug) {
      setSelectedCheckPlan({ ...selectedCheckPlan, status: QcSchemeStatus.optimized })
      setIsEditing(false) // 完成调试后重置编辑状态
    }
    message.success('prompt保存成功')
  }

  const generateAIQualityCheckPrompt = (selectedCheckPlan) => {
    fetchAIQualityCheckPromptTemplate(selectedCheckPlan.type)
  }

  // 编辑调优
  const handleEditClick = () => {
    setIsEditing(true)
    message.success('已进入编辑状态,可以修改 prompt 内容')
  }

  useEffect(() => {
    getAIQualityCheckPlanOption()
  }, [])

  useEffect(() => {
    getAIQualityCheckPlanDetailById(selectedCheckPlan.id)
    getAIQualityCheckPlanOptionHistory(selectedCheckPlan.id)
  }, [selectedCheckPlan.id])

  // 处理agentkey绑定
  const handleBindAgentKey = async () => {
    if (!agentKey.trim()) {
      message.error('请输入AgentKey')
      return
    }
    // 验证通过后绑定
    const [, err] = await bindAgentKey({
      planId: selectedCheckPlan.id!,
      agentKey: agentKey.trim(),
    })
    if (err) {
      message.error(err.message)
      return
    }
    message.success('绑定成功')
  }

  useEffect(() => {
    if (selectedCheckPlan.detail) {
      getAIQualityCheckPrompt(selectedCheckPlan.id)
      if (showHistoryPrompt) getAIQualityCheckPrompt(selectedCheckPlan.id, selectedCheckPlanHistory.version)
      // 设置已保存的agentKey
      setAgentKey(selectedCheckPlan.detail?.agentKey || '')
    }
  }, [selectedCheckPlan.id, selectedCheckPlan.detail])

  return (
    <>
      <PageHeader
        title="大模型调试"
        style={{ width: showHistoryPrompt ? '155%' : '' }}
      />
      <div className="llm-tuning">
        <Card className="mt20 mr20 module">
          <Row align="middle">
            <Col style={{ flexShrink: 0 }}>质检方案：</Col>
            <Col span={20} style={{ flex: 1, maxWidth: '100%' }}>
              <Select
                options={selectedCheckPlan.checkPlanList}
                value={selectedCheckPlan.id || selectedCheckPlan.checkPlanList[0]?.value}
                onChange={(value, option) => {
                  setIsEditing(false) // 切换方案时重置编辑状态
                  setSelectedCheckPlan({
                    id: value,
                    type: option.planType,
                    status: option.status,
                    detail: undefined,
                    prompt: '',
                    checkPlanList: selectedCheckPlan.checkPlanList,
                    name: option.label,
                  })
                }}
              />
            </Col>
          </Row>
          <div className="mt20 text-block" style={{ marginBottom: '30px' }}>
            {selectedCheckPlan.type === QcType.compliance && <PreviewCompliance qcPlanDetail={selectedCheckPlan.detail} />}
            {selectedCheckPlan.type === QcType.moc && <PreviewMoc qcPlanDetail={selectedCheckPlan.detail} />}
            {selectedCheckPlan.type === QcType.scenarioTag && <PreviewScenarioTag qcPlanDetail={selectedCheckPlan.detail} />}
          </div>
          <Row align="middle">
            <Col style={{ flexShrink: 0 }}>历史版本：</Col>
            <Col span={20} style={{ flex: 1, maxWidth: '100%' }}>
              <Select
                key={selectedCheckPlanHistory.version}
                options={selectedCheckPlanHistory.historyPlanList}
                value={selectedCheckPlanHistory.version || selectedCheckPlanHistory.historyPlanList?.[0]?.value}
                onChange={(value) => {
                  setSelectedCheckPlanHistory(prev => ({
                    ...prev,
                    version: value,
                    detail: undefined,
                    prompt: '',
                    historyPlanList: prev.historyPlanList,
                  }))
                  getAIQualityCheckPlanDetailById(selectedCheckPlan.id, value)
                  if (showHistoryPrompt)getAIQualityCheckPrompt(selectedCheckPlan.id, value)
                }}
              />
            </Col>
            <Col style={{ maxWidth: '100%' }}>
              <Button type="primary" className="ml10" onClick={() => {
                if (!showHistoryPrompt)getAIQualityCheckPrompt(selectedCheckPlan.id, selectedCheckPlanHistory.version)
                setShowHistoryPrompt(!showHistoryPrompt)
              }}>提示词
              </Button>
            </Col>
          </Row>
          <div className="mt20 text-block">
            {selectedCheckPlan.type === QcType.compliance && <PreviewCompliance qcPlanDetail={selectedCheckPlanHistory.detail} />}
            {selectedCheckPlan.type === QcType.moc && <PreviewMoc qcPlanDetail={selectedCheckPlanHistory.detail} />}
            {selectedCheckPlan.type === QcType.scenarioTag && <PreviewScenarioTag qcPlanDetail={selectedCheckPlanHistory.detail} />}
          </div>
        </Card>
        <Card className="mt20 mr20 module">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex' }}>
              <Button className="mr10" onClick={() => {
                generateAIQualityCheckPrompt(selectedCheckPlan)
              }}>生成prompt
              </Button>
              <Button className="mr10" onClick={() => {
                saveAIQualityCheckPrompt(selectedCheckPlan.id, false)
              }}>保存prompt
              </Button>
              <Button className="mr10" disabled={selectedCheckPlan.status !== QcSchemeStatus.optimized} onClick={() => {
                handleEditClick()
              }}>编辑调优
              </Button>
              <Button type="primary" disabled={selectedCheckPlan.prompt?.length <= 0} onClick={() => {
                saveAIQualityCheckPrompt(selectedCheckPlan.id, true)
              }}>完成调试
              </Button>
            </div>
            <div style={{ display: 'flex', flexShrink: 0, alignItems: 'center' }}>
              调试结果：
              <Tag color={QcSchemeStatusMap[selectedCheckPlan.status as keyof typeof QcSchemeStatusMap]?.color}>{QcSchemeStatusMap[selectedCheckPlan.status as keyof typeof QcSchemeStatusMap]?.text}</Tag>
            </div>
          </div>
          <Row align="middle" className="mt20">
            <Col style={{ flexShrink: 0 }}>AgentKey：</Col>
            <Col span={12} style={{ flex: 1, maxWidth: '100%' }}>
              <Input
                placeholder="请输入AgentKey"
                value={agentKey}
                onChange={(e) => {
                  setAgentKey(e.target.value)
                }}
              />
            </Col>
            <Col style={{ marginLeft: 10 }}>
              <Button
                onClick={handleBindAgentKey}
                disabled={!agentKey.trim()}
              >
                关联
              </Button>
            </Col>
          </Row>
          <Input.TextArea
            value={selectedCheckPlan.prompt}
            className="mt20 prompt-block"
            disabled={selectedCheckPlan.status === QcSchemeStatus.optimized && !isEditing}
            onChange={(e) => {
              setSelectedCheckPlan(prev => ({
                ...prev,
                prompt: e.target.value,
              }))
            }}
          />
        </Card>
        {
          showHistoryPrompt && (
            <Card className="mt20 module">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <p style={{ lineHeight: '32px' }}>历史prompt：</p>
              </div>
              <Input.TextArea
                value={selectedCheckPlanHistory.prompt}
                className="mt20 prompt-block"
                readOnly
              />
            </Card>
          )
        }
      </div>
    </>
  )
}
export default LlmTuningPage
